import { useState, useEffect, useRef } from "react";
import { useDebouncedEffect } from "../hooks/useDebouncedEffect";
import ProgressTracker from "../components/create-shipment-form/progress-tracker";
import Layout from "../components/layout";
import FormContainer from "../components/create-shipment-form/form-container";
import { generateEmptyFormData } from "@/utils/shipment-form";
import useNoInitialRenderEffect from "../hooks/useNoInitialRenderEffect";
import { useAppContext } from "../context/App-context";
import axios from "axios";
import { useRouter } from "next/router";
import { getSession } from "next-auth/react";
import {
  getUserMeta,
  getUserBoxes,
  getSavedRecipients,
  getDiscountCodeAmount,
  getUnsubmittedForm,
} from "@/lib/db";
import { getUserID } from "@/lib/utils";
import { getTotalPackingCost } from "../lib/calc-packing";
import BirdFlyingLoader from "../components/bird-flying-loader";
import Head from "next/head";
import ShipmentFormNavbar from "../components/create-shipment-form/shipment-form-navbar";
import OverlayPopup from "../components/overlay-popup";
import { RURAL_SURCHARGE_SGD } from "../lib/constants";

export async function getServerSideProps(context) {
  const session = await getSession(context);
  let userID = 0;
  let email = "";
  let retDict = {};
  let userBoxes = [];
  let savedRecipients = [];
  let eligibleDiscount = 0;
  let prevFormRes = null;
  let prevUnsubmittedForm = null;

  if (!session) {
    return {
      redirect: {
        destination: `/register?callbackUrl=${context.resolvedUrl}`,
      },
    };
  }

  if (session) {
    userID = getUserID(session);
    email = session.user.email;
    try {
      retDict = await getUserMeta(userID);
      userBoxes = await getUserBoxes(userID);
      savedRecipients = await getSavedRecipients(userID);
      prevFormRes = await getUnsubmittedForm(userID);

      if (context.query.discount) {
        eligibleDiscount = await getDiscountCodeAmount(context.query.discount);
      }
    } catch (err) {
      console.log(err);
    }
    if (
      prevFormRes &&
      prevFormRes.formVersion ===
        parseInt(process.env.NEXT_PUBLIC_SHIPMENT_FORM_VERSION)
    ) {
      prevUnsubmittedForm = prevFormRes.formData;
    }
  }

  return {
    props: {
      isEdit: context.query?.isEdit ?? false,
      fromGetQuote: context.query?.fromGetQuote ?? false,
      index: context.query.index ?? 0,
      userSavedInfo: {
        senderFirstName: retDict["first_name"] || "",
        senderLastName: retDict["last_name"] || "",
        senderCallCode: retDict["country_code"] || "SG/+65",
        senderMobileNo:
          retDict["subscriber_number"] || retDict["ext_phone"] || "",
        senderEmail: email,
      },
      userBoxes,
      savedRecipients,
      prevUnsubmittedForm,
      eligibleDiscount,
    },
  };
}

export default function CreateShipment({
  isEdit,
  fromGetQuote,
  index,
  userSavedInfo,
  userBoxes,
  savedRecipients,
  prevUnsubmittedForm,
  eligibleDiscount,
}) {
  const [step, setStep] = useState(0);
  const [isFetching, setIsFetching] = useState(true);
  const [showContinueAlert, setShowContinueAlert] = useState(
    !!prevUnsubmittedForm
  );
  const [continuePrevUnsubmitted, setContinuePrevUnsubmitted] = useState(true);
  const { cart, setCart, quoteFormData } = useAppContext();
  const router = useRouter();

  const emptyFormData = generateEmptyFormData();
  const [formData, setFormData] = useState(
    prevUnsubmittedForm ?? {
      ...emptyFormData,
      ...userSavedInfo,
      packages: [{ ...emptyFormData.packages[0] }],
    }
  );

  const getStepHeader = (index) => {
    const stepHeaders = [
      {
        text: "Sender’s Particulars",
        subtext: "(Point of contact for packing and billing)",
      },
      { text: "Recipient’s Particulars", subtext: "" },
      { text: "Package Details", subtext: "" },
      { text: "Add-ons", subtext: "" },
      { text: "AI Shipment Review", subtext: "" },
    ];
    const subtext = stepHeaders[index].subtext;
    return (
      <div className="text-center mb-4 md:mb-6">
        <p id="step-header" className="text-2xl md:text-3xl font-bold">
          {stepHeaders[index].text}
        </p>
        {subtext ? <p className="text-sm">{subtext}</p> : null}
      </div>
    );
  };

  const handleFinishEdit = async () => {
    try {
      const res = await axios.post("/api/submit-shipment-form", {
        data: formData,
        action: "editCart",
      });
      if (res.data.results === "ok") {
        router.push("/dashboard/cart");
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (isEdit) {
      setFormData(cart[index]);
      setShowContinueAlert(false);
    } else if (fromGetQuote) {
      setFormData({
        ...quoteFormData,
        ...userSavedInfo,
      });
      setShowContinueAlert(false);
    }
    setTimeout(() => {
      setIsFetching(false);
    }, 1000);
  }, []);

  useNoInitialRenderEffect(() => {
    if (!continuePrevUnsubmitted) {
      setFormData({
        ...emptyFormData,
        ...userSavedInfo,
      });
      try {
        axios.delete("/api/unsubmitted-forms");
      } catch (err) {
        console.log(err);
      }
    }
  }, [continuePrevUnsubmitted]);

  const saveUnsubmittedForm = async () => {
    try {
      axios.post("/api/unsubmitted-forms", {
        formData,
      });
    } catch (err) {
      console.log(err);
    }
  };

  const isInitialRender = useRef(true);
  useDebouncedEffect(
    () => {
      if (!isInitialRender.current && !isEdit) {
        saveUnsubmittedForm();
      } else {
        isInitialRender.current = false;
      }
    },
    [formData],
    3000
  );

  useNoInitialRenderEffect(() => {
    if (!isEdit) saveUnsubmittedForm();
  }, [step]);

  useDebouncedEffect(
    () => {
      const fetchAreaIsRural = async () => {
        const query = {
          country: formData.recipientCountry,
          city: formData.recipientCity,
          postalCode: formData.recipientPostalCode,
        };
        const res = await axios.post("/api/rural-surcharge", query);
        setFormData((formData) => ({ ...formData, isRural: res.data.results }));
      };
      if (
        formData.recipientCountry &&
        formData.recipientPostalCode &&
        formData.recipientCity
      ) {
        fetchAreaIsRural();
      }
    },
    [
      formData.recipientCountry,
      formData.recipientPostalCode,
      formData.recipientCity,
    ],
    300
  );

  const totalPackingCost = getTotalPackingCost(formData.packages);
  const totalDutiesAndTaxes =
    formData.dutiesPaidBy === "SENDER"
      ? formData.dutyHandlingFee + formData.taxes + formData.duties
      : 0;
  const insuranceAmount = formData.isInsured ? formData.insuranceAmount : 0;
  const ruralSurchargeAmount = formData.isRural ? RURAL_SURCHARGE_SGD : 0;

  useEffect(() => {
    const calcDiscountAmount = () => {
      let initialDiscountAmount = 0;
      let discountCode = "";

      if (process.env.NEXT_PUBLIC_CHRISTMAS_PROMOTION === "1") {
        const getChristmasDiscount = (shipmentFreightAndPackingQuote) => {
          if (shipmentFreightAndPackingQuote >= 400) {
            return 32;
          } else if (shipmentFreightAndPackingQuote >= 300) {
            return 22.5;
          } else if (shipmentFreightAndPackingQuote >= 160) {
            return 12;
          } else {
            return 0;
          }
        };
        const shipmentFreightAndPackingQuote =
          formData.initialFreightQuote +
          ruralSurchargeAmount +
          totalPackingCost;
        initialDiscountAmount = getChristmasDiscount(
          shipmentFreightAndPackingQuote
        );
      }

      if (eligibleDiscount > 0) {
        const MIN_PAYMENT_AMOUNT = 0.5; // minimum amount for stripe checkout
        const shipmentPreDiscountTotal =
          formData.initialFreightQuote +
          ruralSurchargeAmount +
          totalPackingCost +
          insuranceAmount +
          totalDutiesAndTaxes;
        initialDiscountAmount = Math.min(
          eligibleDiscount,
          Math.max(shipmentPreDiscountTotal - MIN_PAYMENT_AMOUNT, 0)
        );
        if (initialDiscountAmount > 0) discountCode = router.query.discount;
      }

      setFormData((formData) => ({
        ...formData,
        discountCode: discountCode,
        initialDiscountAmount: initialDiscountAmount,
      }));
    };

    calcDiscountAmount();
  }, [
    formData.initialFreightQuote,
    ruralSurchargeAmount,
    totalPackingCost,
    insuranceAmount,
    totalDutiesAndTaxes,
    eligibleDiscount,
    router.query.discount,
  ]);

  return (
    <Layout showFooter={false}>
      <Head>
        <title>
          Create Shipment | JustShip | All-In-One International Shipping
        </title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <meta
          name="description"
          content="JustShip offers the cheapest all-in-one hassle-free international shipping. Fill in details to create a shipment and get your parcel delivered quickly"
        />
        <meta
          property="og:title"
          content="Create Shipment | JustShip | All-In-One International Shipping"
        />
        <meta
          property="og:description"
          content="JustShip offers the cheapest all-in-one hassle-free international shipping. Fill in details to create a shipment and get your parcel delivered quickly"
        />
      </Head>
      {!isFetching ? (
        <>
          <div className="top-0 z-10 sticky bg-white shadow-sm">
            <ShipmentFormNavbar
              formData={formData}
              packages={formData.packages}
              country={formData.recipientCountry}
            />
          </div>
          <div className="bg-background-gray z-0 min-h-screen">
            <ProgressTracker step={step} />
            {getStepHeader(step)}
            {showContinueAlert && (
              <OverlayPopup
                title="Continue where you left off?"
                noText="No"
                yesText="Continue"
                actionOnNo={() => {
                  setContinuePrevUnsubmitted(false);
                  setShowContinueAlert(false);
                }}
                actionOnYes={() => {
                  setContinuePrevUnsubmitted(true);
                  setShowContinueAlert(false);
                }}
                useEnter={true}
                isAlert={true}
              >
                You have entered some data in the form. Do you want to continue
                from where you left off?
              </OverlayPopup>
            )}
            <FormContainer
              step={step}
              formData={formData}
              setFormData={setFormData}
              setStep={setStep}
              isEdit={isEdit}
              handleFinishEdit={handleFinishEdit}
              userBoxes={userBoxes}
              savedRecipients={savedRecipients}
            />
          </div>
        </>
      ) : (
        <>
          <BirdFlyingLoader />
          <div className="bg-background-gray min-h-screen md:min-h-[100vh]"></div>
        </>
      )}
    </Layout>
  );
}
